// lib/ads/partners.ts
// Central registry of partner creatives = ad inventory for the in-app slots (feed, discover,
// profile hero). Add a partner here (or set weight) to rotate it into the slots — no component
// changes needed. weight = share of voice (0 disables without deleting).
//
// 2026-09-08: the Balkan Bug sister sites (DayProp, Swiss Prime Markets, Predvidi.me) joined the
// rotation next to FXDoctor. Their creatives are SVG (public/assets/partners/<id>/) so they stay
// crisp at any DPI and weigh ~2 KB. See src/lib/ads/network.ts for the site registry.

export interface PartnerCreative {
  /** stable id, also used as utm_campaign + GA promotion_id */
  id: string;
  name: string;
  /** destination (UTM added automatically by the slot) */
  href: string;
  /** 300x250 medium rectangle (mobile slot) */
  mobileSrc: string;
  /** 728x90 leaderboard (tablet/desktop slot) */
  desktopSrc: string;
  /** 1200x628 cover (profile hero + OG share image) */
  heroSrc?: string;
  /** if this partner owns a profile page, its username (lowercase) */
  profileUsername?: string;
  alt: string;
  /** share of voice for rotation; default 1, 0 = disabled */
  weight?: number;
}

export const FXDOCTOR: PartnerCreative = {
  id: 'fxdoctor',
  name: 'FXDoctor',
  href: 'https://fxdoctor.pro',
  mobileSrc: '/assets/partners/fxdoctor/fxdoctor_300x250.png',
  desktopSrc: '/assets/partners/fxdoctor/fxdoctor_728x90.png',
  heroSrc: '/assets/partners/fxdoctor/fxdoctor_1200x628.png',
  profileUsername: 'fxdoctor',
  alt: 'FXDoctor — independent trading education. Master the markets, no shortcuts. Free intro session at fxdoctor.pro',
  weight: 3,
};

export const DAYPROP: PartnerCreative = {
  id: 'dayprop',
  name: 'DayProp',
  href: 'https://dayprop.net',
  mobileSrc: '/assets/partners/dayprop/dayprop_300x250.svg',
  desktopSrc: '/assets/partners/dayprop/dayprop_728x90.svg',
  alt: 'DayProp — trguj našim novcem, do 25.000 $, bez testa i bez faza. dayprop.net',
  weight: 2,
};

export const SWISSPRIME: PartnerCreative = {
  id: 'swissprime',
  name: 'Swiss Prime Markets',
  href: 'https://swissprimemarkets.com',
  mobileSrc: '/assets/partners/swissprime/swissprime_300x250.svg',
  desktopSrc: '/assets/partners/swissprime/swissprime_728x90.svg',
  alt: 'Swiss Prime Markets — fjučers prop trejding na CME, isplate od Faze 1, do 90% podele dobiti.',
  weight: 1,
};

export const PREDVIDI: PartnerCreative = {
  id: 'predvidi',
  name: 'Predvidi.me',
  href: 'https://predvidi.me',
  mobileSrc: '/assets/partners/predvidi/predvidi_300x250.svg',
  desktopSrc: '/assets/partners/predvidi/predvidi_728x90.svg',
  alt: 'Predvidi.me — tržišta predviđanja na srpskom: politika, sport, ekonomija.',
  weight: 1,
};

export const PARTNERS: PartnerCreative[] = [FXDOCTOR, DAYPROP, SWISSPRIME, PREDVIDI];

/** Weighted-random pick for slot rotation. Returns null if inventory is empty. */
export function pickPartner(): PartnerCreative | null {
  const active = PARTNERS.filter((p) => (p.weight ?? 1) > 0);
  if (active.length === 0) return null;
  const total = active.reduce((sum, p) => sum + (p.weight ?? 1), 0);
  let r = Math.random() * total;
  for (const p of active) {
    r -= p.weight ?? 1;
    if (r <= 0) return p;
  }
  return active[active.length - 1];
}

/** Find the partner that owns a given profile username (for the profile hero). */
export function partnerByUsername(username?: string | null): PartnerCreative | null {
  if (!username) return null;
  const u = username.toLowerCase();
  return PARTNERS.find((p) => p.profileUsername?.toLowerCase() === u) ?? null;
}
